import React, { useState, useEffect, useRef } from "react";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
//import AppBar from "@mui/material/AppBar";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import Badge from "@material-ui/core/Badge";
import Tooltip from "@material-ui/core/Tooltip";
import NotificationsIcon from "@material-ui/icons/Notifications";
import { useDispatch, useSelector } from "react-redux";
import NotificationsNoneOutlinedIcon from "@material-ui/icons/NotificationsNoneOutlined";
import SettingsIcon from "@material-ui/icons/Settings";

import TabsMenu from "../TabsMenu";
import defaultLogo from "../../assets/new-logo.png";
import {
  getUserName,
  getUserPicture,
  getUserEmail,
  logout,
} from "../../services/UserService";
import UserAvatar from "../profile/UserAvatar";
import SearchBox from "./SearchBox";
import { setModalState } from "../../store/modal/ModalActions";
import {
  shouldUpdateEditSequenceList,
  selectCurrentUserSavingCategoryIds,
} from "../../store/product-list/ProductListSelectors";
import { useLocation } from "react-router-dom";
import { isLogoutSupported } from "../../services/UserService";
import MenuUI from "./MenuUI";
import {
  selectAccountFeatures,
  selectAppLogo,
  selectCallerAccountId,
  selectConfigValue,
  selectIsProductReindexFeatureEnabled,
} from "../../store/app-config/AppConfigSelectors";
import { submitSearchReindex } from "../../store/search-reindex/SearchReindexActions";
import {
  selectIsSubmittingSearchReindex,
  selectLastSearchReindexResult,
  selectLatestSearchReindex,
  selectRunningSearchReindex,
} from "../../store/search-reindex/SearchReindexSelectors";
import { addGlobalAlertState } from "../../store/global-alert/GlobalAlertActions";
import ReindexElapsedTime from "./ReindexElapsedTime";
import { formatReindexTime } from "./formatReindexTime";
import ButtonComponent from "../common/ButtonComponent";
import { selectProductAlertsListFetched } from "../../store/product-alerts-modal/ProductAlertsListSelectors";
import AppState from "../../store/AppState";
import InteractiveMenu from "../common/InteractiveMenu";
import { LogoutConfirmationModalId } from "./LogoutConfirmationModal";
import Icons from "components/common/Icons";
import { CDN_WIDGET_URL, removeDotFromStart } from "utils/WidgetUtil";
import { RecommendationsIcon } from "assets";
import { useIntl } from "react-intl";
import { selectCurrentStoreId } from "store/store-list/StoreListSelectors";
import { useHasEntitlement } from "../../hooks/useEntitlement";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    grow: {
      flexGrow: 1,
    },
    root: {
      color: "black",
    },
    logo: {
      maxHeight: "64px",
      maxWidth: "121px",
      marginRight: "40px",
    },
    menuButton: {
      minWidth: "100px",
    },

    sectionDesktop: {
      display: "none",
      [theme.breakpoints.up("md")]: {
        display: "flex",
      },
    },
    sectionMobile: {
      display: "flex",
      [theme.breakpoints.up("md")]: {
        display: "none",
      },
    },
    logoutIcon: {
      height: 50,
      width: 50,
      marginTop: 5,
      marginLeft: 2,
    },
    disabledIcon: {
      opacity: 0.3,
      cursor: "not-allowed",
    },
    reindexButton: {
      display: "none",
      position: "absolute",
      left: "50%",
      transform: "translateX(-50%)",
      flexDirection: "column",
      alignItems: "center",
      [theme.breakpoints.up("md")]: {
        display: "inline-flex",
      },
    },
    reindexButtonMobile: {
      display: "inline-flex",
      flexDirection: "column",
      alignItems: "center",
      marginRight: 8,
    },
    reindexElapsed: {
      fontSize: 12,
      lineHeight: "16px",
      color: theme.palette.text.secondary,
    },
  }),
);

interface HeaderProps {
  "data-layout-role"?: string;
}

export default function Header(props: HeaderProps) {
  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  //const [configAnchorEl, setConfigAnchorEl] = React.useState<string>("");
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const [menuIconSelected, setMenuIconSelected] = useState<string>("");
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);
  const shouldUpdateEditSequenceListFlag = useSelector(
    shouldUpdateEditSequenceList,
  );
  const savingCategoryIds = useSelector(selectCurrentUserSavingCategoryIds);
  const appLogo = useSelector(selectAppLogo);
  const notificationsData = useSelector(selectProductAlertsListFetched);
  const accountId = useSelector(selectCallerAccountId);
  const features = useSelector((state: AppState) =>
    selectAccountFeatures(state, accountId),
  );
  const isGlobalRecommendationsFeatureEnabled =
    features?.["global-recommendations"] ?? false;

  const hasCatalogEntitlement = useHasEntitlement("catalog");
  const hasAnalyticsEntitlement = useHasEntitlement("analytics");

  const location = useLocation();
  const url = location.pathname;
  const isInReportsTab = url.includes("reports");
  const isProductAlertsEnabled = useSelector((state: AppState) =>
    selectConfigValue(state, "enableProductAlerts"),
  );
  const currentStoreId = useSelector(selectCurrentStoreId);
  const isProductReindexEnabled = useSelector(
    selectIsProductReindexFeatureEnabled,
  );
  const isSubmittingReindex = useSelector(selectIsSubmittingSearchReindex);
  const runningReindex = useSelector(selectRunningSearchReindex);
  const lastReindexResult = useSelector(selectLastSearchReindexResult);
  const latestReindex = useSelector(selectLatestSearchReindex);

  useEffect(() => {
    if (!lastReindexResult) {
      return;
    }
    const alertProps =
      lastReindexResult.status === "done"
        ? {
            descriptor: {
              id: "header.productReindexComplete",
              defaultMessage: "Product reindex complete: {jobId}.",
            },
            variables: { jobId: lastReindexResult.executionId ?? "" },
            severity: "success" as const,
          }
        : lastReindexResult.errorMessage
          ? {
              message: lastReindexResult.errorMessage,
              severity: "error" as const,
            }
          : {
              descriptor: {
                id: "header.productReindexFailed",
                defaultMessage: "Product reindex failed. Please try again.",
              },
              severity: "error" as const,
            };
    dispatch(
      addGlobalAlertState({
        alertsProps: [{ ...alertProps, variant: "standard" }],
      }),
    );
  }, [lastReindexResult, dispatch]);

  const reindexActionHandler = () => {
    dispatch(submitSearchReindex());
  };

  const latestReindexText = (): string => {
    if (latestReindex === undefined) {
      return "";
    }
    if (latestReindex === null) {
      return intl.formatMessage({
        id: "header.productReindexLatestNone",
        defaultMessage: "No reindex has been run from Smart Merchandiser yet.",
      });
    }
    const { storeId, status, executionId, startedAt, finishedAt, timeZone } =
      latestReindex;
    if (status === "unknown") {
      return intl.formatMessage(
        {
          id: "header.productReindexLatestUnknown",
          defaultMessage:
            "Last reindex: {storeId}, started {time}. Unknown. Job ID: {jobId}. Check Business Manager for the final result.",
        },
        {
          storeId,
          time: formatReindexTime(startedAt, timeZone),
          jobId: executionId,
        },
      );
    }
    return intl.formatMessage(
      status === "done"
        ? {
            id: "header.productReindexLatestDone",
            defaultMessage:
              "Last reindex: {storeId}, {time}. Done. Job ID: {jobId}.",
          }
        : {
            id: "header.productReindexLatestFailed",
            defaultMessage:
              "Last reindex: {storeId}, {time}. Failed. Job ID: {jobId}.",
          },
      {
        storeId,
        time: formatReindexTime(finishedAt, timeZone),
        jobId: executionId,
      },
    );
  };

  const renderReindexTooltip = () => {
    const lines = [
      !currentStoreId
        ? intl.formatMessage({
            id: "header.productReindexSelectStore",
            defaultMessage:
              "Please select a store in the Catalog tab to reindex products.",
          })
        : "",
      latestReindexText(),
    ].filter(Boolean);
    if (lines.length === 0) {
      return "";
    }
    return (
      <>
        {lines.map((line) => (
          <div key={line}>{line}</div>
        ))}
      </>
    );
  };

  const renderReindexButton = (className: string) => (
    <Tooltip title={renderReindexTooltip()} arrow>
      <span className={className}>
        <ButtonComponent
          color="primary"
          variant="contained"
          disabled={!currentStoreId || isSubmittingReindex || !!runningReindex}
          onClick={reindexActionHandler}
        >
          {intl.formatMessage({
            id: "header.productReindex",
            defaultMessage: "Reindex",
          })}
        </ButtonComponent>
        {runningReindex && (
          <ReindexElapsedTime
            startedAt={runningReindex.startedAt}
            className={classes.reindexElapsed}
          />
        )}
      </span>
    </Tooltip>
  );

  // Ref to track if logout was explicitly confirmed (to prevent browser warning on programmatic logout)
  const logoutConfirmedRef = useRef(false);

  // Expose the ref to window so LogoutConfirmationModal can access it
  useEffect(() => {
    (window as any).__logoutConfirmedRef = logoutConfirmedRef;
    return () => {
      delete (window as any).__logoutConfirmedRef;
    };
  }, []);

  // Handle browser exit warning when save is in progress
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Don't show browser warning if logout was explicitly confirmed
      if (logoutConfirmedRef.current) {
        return;
      }
      // Show browser warning if a save is in progress
      if (savingCategoryIds.length > 0) {
        e.preventDefault();
        return ""; // Some browsers require a return value
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [savingCategoryIds]);

  const handleProfileMenuOpen = (
    value: string,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    if (value !== "") {
      setMenuIconSelected(value);
      setAnchorEl(event.currentTarget);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleRecommendationMenuOpen = (
    value: string,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    if (value !== "") {
      setMenuIconSelected(value);
      setAnchorEl(event.currentTarget);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleConfigMenuOpen = (
    value: string,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    setMenuIconSelected(value);
    setAnchorEl(event?.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
    setMenuIconSelected("");
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const openProductAlertsListModal = () => {
    dispatch(setModalState("ProductAlertsListModal", true));
  };

  const logoutSupported = isLogoutSupported();
  const menuId = "primary-search-account-menu";

  const mobileMenuId = "primary-search-account-menu-mobile";
  const renderMobileMenu = (
    <InteractiveMenu
      menuId="MenuItemWithSubItems"
      handleClose={handleMobileMenuClose}
      open={isMobileMenuOpen}
      classes={classes}
      anchorEl={mobileMoreAnchorEl}
      mobileMenuId={mobileMenuId}
      logoutSupported={logoutSupported}
      isInReportsTab={isInReportsTab}
      shouldUpdateEditSequenceListFlag={shouldUpdateEditSequenceListFlag}
      handleMenuClose={handleMenuClose}
      handleProfileMenuOpen={handleProfileMenuOpen}
    />
  );

  return (
    <div
      className={classes.grow}
      style={{ margin: 0 }}
      data-layout-role={props["data-layout-role"]}
    >
      <AppBar
        style={{ background: "#fff" }}
        position="static"
        className={classes.root}
      >
        <Toolbar>
          <img
            src={
              appLogo || `${CDN_WIDGET_URL}${removeDotFromStart(defaultLogo)}`
            }
            alt="Smart Merchandiser Logo"
            className={classes.logo}
          />
          <TabsMenu />
          {isProductReindexEnabled &&
            renderReindexButton(classes.reindexButton)}
          <SearchBox />
          <div className={classes.sectionDesktop}>
            {hasCatalogEntitlement &&
              isProductAlertsEnabled === "true" &&
              (notificationsData.length ? (
                <IconButton
                  onClick={openProductAlertsListModal}
                  aria-label={`show ${notificationsData[0].SKUs.length} new notifications`}
                  color="inherit"
                >
                  <Badge
                    badgeContent={notificationsData[0].SKUs.length}
                    color="primary"
                  >
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
              ) : (
                <IconButton
                  onClick={openProductAlertsListModal}
                  aria-label={`show ${notificationsData.length} new notifications`}
                  color="inherit"
                >
                  <NotificationsNoneOutlinedIcon />
                </IconButton>
              ))}
            {hasAnalyticsEntitlement &&
              isGlobalRecommendationsFeatureEnabled && (
                <Tooltip
                  title={
                    !currentStoreId
                      ? intl.formatMessage({
                          id: "header.recommendationsSelectStore",
                          defaultMessage:
                            "Please select a store in the Catalog tab to view the Recommendations.",
                        })
                      : intl.formatMessage({
                          id: "header.globalRecommendations",
                          defaultMessage: "Global Recommendations",
                        })
                  }
                  arrow
                >
                  <span style={{ display: "inline-flex" }}>
                    <IconButton
                      aria-label="recommendation"
                      id="recommendation"
                      onClick={(e) =>
                        handleRecommendationMenuOpen("recommendation", e)
                      }
                      disabled={!currentStoreId}
                      className={!currentStoreId ? classes.disabledIcon : ""}
                    >
                      <RecommendationsIcon width={21} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            {hasCatalogEntitlement && (
                <IconButton
                  aria-label="config-menu"
                  id="config-menu"
                  onClick={(e) => handleConfigMenuOpen("config-menu", e)}
                >
                  <SettingsIcon />
                </IconButton>
              )}

            <IconButton
              edge="end"
              aria-label="account of current user"
              aria-controls={menuId}
              aria-haspopup="true"
              onClick={(e) => handleProfileMenuOpen("profile", e)}
              color="inherit"
            >
              <UserAvatar
                name={getUserName()}
                picture={getUserPicture()}
                email={getUserEmail()}
              />
            </IconButton>
            {logoutSupported && (
              <IconButton
                edge="end"
                aria-label="log out"
                aria-haspopup="true"
                onClick={() => {
                  // Show warning if there are unsaved changes OR if a save is in progress
                  const isSaving = savingCategoryIds.length > 0;
                  if (!isInReportsTab && (shouldUpdateEditSequenceListFlag || isSaving)) {
                    dispatch(setModalState(LogoutConfirmationModalId, true));
                  } else {
                    logout();
                  }
                  handleMenuClose();
                }}
                color="inherit"
                className={classes.logoutIcon}
              >
                <Icons iconId="SignoutIcon" />
              </IconButton>
            )}
          </div>
          <div className={classes.sectionMobile}>
            {isProductReindexEnabled &&
              renderReindexButton(classes.reindexButtonMobile)}
            <IconButton
              aria-label="show more"
              aria-controls={mobileMenuId}
              aria-haspopup="true"
              onClick={handleMobileMenuOpen}
              color="inherit"
            >
              <Icons iconId="MoreVerticalIcon" />
            </IconButton>
          </div>
        </Toolbar>
      </AppBar>
      {renderMobileMenu}
      <MenuUI
        anchor={anchorEl}
        selected={menuIconSelected}
        handleClose={handleMenuClose}
      />
    </div>
  );
}
